import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { DetalleAsambleaPdf, DetallePreguntaPdf, DetalleRespuestaPdf, DetalleParticipantePdf } from '../../../../api/asambleaService';
import logo from '../../../../assets/logo.jpeg';

// Registrar una fuente si es necesario, o usar las estándar
// Font.register({ family: 'Helvetica', fontWeight: 'normal' });

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 8,
    fontFamily: 'Helvetica',
    color: '#333',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2pt solid #3d5a40',
    paddingBottom: 10,
  },
  logo: {
    width: 120,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#3d5a40',
    textAlign: 'right',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#3d5a40',
    backgroundColor: '#f3f4f6',
    padding: 5,
    marginBottom: 10,
    borderRadius: 3,
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  infoItem: {
    width: '50%',
    marginBottom: 5,
  },
  label: {
    fontWeight: 'bold',
    color: '#666',
  },
  value: {
    color: '#000',
  },
  table: {
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableColHeader: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: '#f9fafb',
    padding: 3,
  },
  tableCol: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
  },
  tableCellHeader: {
    fontWeight: 'bold',
    fontSize: 7,
  },
  tableCell: {
    fontSize: 7,
  },
  questionBox: {
    marginBottom: 15,
    padding: 10,
    border: '1pt solid #e5e7eb',
    borderRadius: 5,
  },
  questionText: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#111827',
  },
  answerBox: {
    marginLeft: 15,
    marginTop: 5,
    paddingLeft: 10,
    borderLeft: '2pt solid #3d5a40',
  },
  answerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  answerText: {
    fontWeight: 'bold',
  },
  votes: {
    color: '#3d5a40',
    fontWeight: 'bold',
  },
  votersList: {
    fontSize: 8,
    color: '#6b7280',
    marginTop: 2,
  },
  winnerBadge: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontSize: 7,
    padding: '1 4',
    borderRadius: 2,
    marginLeft: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
    borderTop: '1pt solid #e5e7eb',
    paddingTop: 10,
  }
});

interface AsambleaPDFProps {
  data: DetalleAsambleaPdf;
}

const AsambleaPDF = ({ data }: AsambleaPDFProps) => (
  <Document title={`Reporte Asamblea - ${data.asa_titulo}`}>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Image src={logo} style={styles.logo} />
        <View>
          <Text style={styles.title}>Resumen de Asamblea</Text>
          <Text style={{ textAlign: 'right', fontSize: 10, color: '#666' }}>ID: {data.asa_codigo}</Text>
        </View>
      </View>

      {/* Información General */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Resumen Informativo</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Título: </Text><Text style={styles.value}>{data.asa_titulo}</Text></Text>
          </View>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Estado: </Text><Text style={styles.value}>{data.asa_estatus_nombre}</Text></Text>
          </View>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Unidad: </Text><Text style={styles.value}>{data.asa_unidad_residencial_nombre}</Text></Text>
          </View>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Llave: </Text><Text style={styles.value}>{data.asa_llave}</Text></Text>
          </View>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Administrador: </Text><Text style={styles.value}>{data.asa_administrador_nombre}</Text></Text>
          </View>
          <View style={styles.infoItem}>
            <Text><Text style={styles.label}>Moderador: </Text><Text style={styles.value}>{data.asa_moderador_nombre}</Text></Text>
          </View>
        </View>
        <Text style={{ marginTop: 5 }}><Text style={styles.label}>Descripción: </Text><Text style={styles.value}>{data.asa_descripcion}</Text></Text>
      </View>

      {/* Participantes */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Participantes ({data.lstParticipantes.length})</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.tableColHeader, { width: '40%' }]}><Text style={styles.tableCellHeader}>Nombre</Text></View>
            <View style={[styles.tableColHeader, { width: '20%' }]}><Text style={styles.tableCellHeader}>Torre</Text></View>
            <View style={[styles.tableColHeader, { width: '20%' }]}><Text style={styles.tableCellHeader}>Apto</Text></View>
            <View style={[styles.tableColHeader, { width: '20%' }]}><Text style={styles.tableCellHeader}>Tipo</Text></View>
          </View>
          {data.lstParticipantes.map((p, i) => (
            <View style={styles.tableRow} key={i}>
              <View style={[styles.tableCol, { width: '40%' }]}><Text style={styles.tableCell}>{p.pas_usuario_nombre}</Text></View>
              <View style={[styles.tableCol, { width: '20%' }]}><Text style={styles.tableCell}>{p.pas_torre_nombre}</Text></View>
              <View style={[styles.tableCol, { width: '20%' }]}><Text style={styles.tableCell}>{p.pas_apartamento_nombre}</Text></View>
              <View style={[styles.tableCol, { width: '20%' }]}><Text style={styles.tableCell}>{p.pas_tipo_usuario_nombre}</Text></View>
            </View>
          ))}
        </View>
      </View>

      {/* Preguntas y Respuestas */}
      <View style={styles.section} break>
        <Text style={styles.sectionTitle}>Resultados de Votación</Text>
        {data.lstPreguntas.map((q, i) => (
          <View style={styles.questionBox} key={i} wrap={false}>
            <Text style={styles.questionText}>Pregunta {i+1}: {q.pra_texto}</Text>
            <Text style={{ fontSize: 8, color: '#666', marginBottom: 5 }}>Estatus: {q.pra_estatus_nombre} | Inicio: {q.pra_fecha_inicio} | Fin: {q.pra_fecha_cierre}</Text>
            
            {q.lstRespuestas.map((r, ri) => (
              <View style={styles.answerBox} key={ri}>
                <View style={styles.answerHeader}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.answerText}>{r.rpa_orden}. {r.rpa_texto}</Text>
                    {r.rpa_ganadora === "1" && (
                      <View style={styles.winnerBadge}><Text>GANADORA</Text></View>
                    )}
                  </View>
                  <Text style={styles.votes}>{r.erp_total} votos</Text>
                </View>
                {r.lstParticipantesEnEstaRespouesta.length > 0 && (
                  <Text style={styles.votersList}>
                    Votantes: {r.lstParticipantesEnEstaRespouesta.map(v => {
                      const torre = v.pas_torre_nombre.replace(/torre\s*/i, '').trim();
                      const apto = v.pas_apartamento_nombre.replace(/apto\s*|apartamento\s*/i, '').trim();
                      return `${torre}-${apto}`;
                    }).join(', ')}
                  </Text>
                )}
              </View>
            ))}
          </View>
        ))}
      </View>

      {/* Footer */}
      <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
        `Página ${pageNumber} de ${totalPages} - Generado por EasyPH`
      )} fixed />
    </Page>
  </Document>
);

export default AsambleaPDF;
